import React, { useState, useEffect } from 'react';
import Header from '../../../components/Header';
import Footer from '../../../components/Footer';

// A página pública de carreiras da Entrega Já no TOTVS RH Atração de Talentos
// (linha Feedz). O candidato termina lá de qualquer jeito -- é onde ele se
// inscreve -- então o endereço também serve de saída quando a leitura falha.
const PAGINA_DE_CARREIRAS =
    'https://atracaodetalentos.totvs.app/trabalheconoscoentregaja/extended';

// O proxy do Caddy, que busca a página acima pelo servidor. Existe por causa do
// CORS: a página é pública, mas não manda Access-Control-Allow-Origin, e o
// navegador recusa lê-la de outro domínio.
const ORIGEM_DAS_VAGAS = '/vagas-feedz';

// Aquele ATS não expõe JSON: /api/job-opportunities e as variantes respondem
// 302. O que existe é a página renderizada no servidor, e nela cada vaga é um
// <div> com os campos em data-attributes -- que é o que lemos aqui, em vez de
// raspar os textos visíveis, que mudam de lugar a cada ajuste de layout deles.
function lerVagas(html) {
    const pagina = new DOMParser().parseFromString(html, 'text/html');
    const itens = pagina.querySelectorAll('.index__job-opportunities__grid__item');

    return Array.from(itens).map((item) => {
        const d = item.dataset;
        const link = item.querySelector('a.index__job-opportunity-name');
        const cidade = d.cityName || '';
        const uf = d.stateSmallName || '';

        return {
            id: d.id || d.code,
            codigo: d.code || '',
            titulo: d.title || (link ? link.textContent.trim() : ''),
            cargo: d.positionName || '',
            departamento: d.departmentName || '',
            // hideLocation chega como a string "True"/"False", não como booleano
            local: d.hideLocation === 'True' ? '' : [cidade, uf].filter(Boolean).join(', '),
            url: (link && link.href) || PAGINA_DE_CARREIRAS,
        };
    });
}

// ⚠️ **Só entram aqui classes que existem em /assets/css/tailwind.css.** Aquele
// arquivo é um build com purge, servido pronto: ele tem apenas as classes que o
// site original usava, e não é recompilado. Classe inventada não vira erro --
// vira estilo que simplesmente não existe. Foi o que desmontou a primeira
// versão desta lista: sem `flex-col`, o container ficou em linha e cada card
// virou um amontoado. Conferir antes com:
//     grep -o '\.nome-da-classe' public/assets/css/tailwind.css
// Sem correspondente na folha, usar `style` -- é o caso de overflow e
// white-space abaixo.
function LinhaDaVaga({ vaga }) {
    return (
        <tr className="border-b border-[#f1f1f1]">
            <td className="py-4 px-4">
                <a
                    href={vaga.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-base font-semibold text-dark hover:text-primary"
                >
                    {vaga.titulo}
                </a>
                {vaga.cargo && (
                    <span className="block text-sm text-body-color">{vaga.cargo}</span>
                )}
            </td>
            <td className="py-4 px-4 text-base text-body-color">{vaga.departamento}</td>
            <td className="py-4 px-4 text-base text-body-color">{vaga.local}</td>
            <td className="py-4 px-4">
                <a
                    href={vaga.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ whiteSpace: 'nowrap' }}
                    className="inline-flex items-center justify-center rounded bg-primary py-2 px-6 text-base font-medium text-white transition duration-300 ease-in-out hover:bg-dark"
                >
                    Quero me candidatar
                </a>
            </td>
        </tr>
    );
}

function TabelaDeVagas({ vagas }) {
    return (
        // A tabela tem quatro colunas e não cabe em tela de celular. Rolagem
        // lateral no container preserva as linhas inteiras -- melhor do que
        // espremer o título da vaga até ele quebrar letra a letra.
        <div style={{ overflowX: 'auto', marginTop: 40 }}>
            <table className="w-full text-left">
                <thead>
                    <tr className="bg-[#f5f8ff]">
                        <th className="py-3 px-4 text-sm font-semibold text-dark">Vaga</th>
                        <th className="py-3 px-4 text-sm font-semibold text-dark">Área</th>
                        <th className="py-3 px-4 text-sm font-semibold text-dark">Local</th>
                        <th className="py-3 px-4"></th>
                    </tr>
                </thead>
                <tbody>
                    {vagas.map((vaga) => (
                        <LinhaDaVaga key={vaga.id} vaga={vaga} />
                    ))}
                </tbody>
            </table>
        </div>
    );
}

export default function Oportunidades() {
    // 'carregando' | 'ok' | 'falhou'
    const [situacao, setSituacao] = useState('carregando');
    const [vagas, setVagas] = useState([]);

    useEffect(() => {
        // Se o componente sair da tela antes da resposta chegar, atualizar o
        // estado avisa no console e não serve para nada.
        let vivo = true;

        fetch(ORIGEM_DAS_VAGAS)
            .then((r) => {
                if (!r.ok) throw new Error('a origem das vagas respondeu ' + r.status);
                return r.text();
            })
            .then((html) => {
                if (!vivo) return;
                setVagas(lerVagas(html));
                setSituacao('ok');
            })
            .catch(() => {
                if (vivo) setSituacao('falhou');
            });

        return () => { vivo = false; };
    }, []);

    return (
        <>
            <Header />
            <div className="relative z-10 overflow-hidden bg-primary pt-[120px] pb-[100px] md:pt-[130px] lg:pt-[160px]">
                <div className="container">
                    <div className="-mx-4 flex flex-wrap items-center">
                        <div className="w-full px-4">
                            <div className="text-center">
                                <h1 className="text-4xl font-semibold text-white">Venha fazer parte do nosso time</h1>
                                <h1 className="text-4xl font-semibold text-white">Envio de currículo, vagas disponiveis e muito mais..</h1>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <section className="pt-20 pb-10 lg:pt-[120px] lg:pb-20">
                <div className="container">
                    <h2 className="mb-4 text-3xl font-bold text-dark sm:text-4xl md:text-[42px]">
                        Vagas Disponíveis
                    </h2>

                    {situacao === 'carregando' && (
                        <p className="text-base text-body-color">Buscando as vagas abertas...</p>
                    )}

                    {situacao === 'ok' && vagas.length > 0 && (
                        <p className="text-base text-body-color">Clique na vaga para se candidatar</p>
                    )}

                    {/* Sem vaga aberta é um estado normal, e não um defeito: a
                        pessoa precisa saber que a página funcionou e que hoje
                        não há nada, em vez de encarar um espaço vazio. */}
                    {situacao === 'ok' && vagas.length === 0 && (
                        <p className="text-base text-body-color">
                            No momento não há vagas abertas. Acompanhe esta página ou envie
                            seu currículo para{' '}
                            <a className="text-primary" href="mailto:comercial@entregaja.com.br">
                                comercial@entregaja.com.br
                            </a>.
                        </p>
                    )}

                    {/* A lista pode falhar, mas a candidatura não pode morrer
                        junto: o caminho para a página de carreiras continua
                        aberto, com um clique a mais. */}
                    {situacao === 'falhou' && (
                        <p className="text-base text-body-color">
                            Não conseguimos carregar as vagas agora.{' '}
                            <a
                                className="text-primary underline"
                                href={PAGINA_DE_CARREIRAS}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Veja as vagas abertas direto no nosso portal de carreiras
                            </a>.
                        </p>
                    )}

                    {vagas.length > 0 && <TabelaDeVagas vagas={vagas} />}
                </div>
            </section>

            <Footer />
        </>
    );
}
